import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSalaryRules() {
  const [rules, setRules] = useState({
    late_threshold_minutes: 15,
    lates_for_half_day: 3,
    working_hours_per_day: 8.0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/salary-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data);
    } catch (error) {
      toast.error('Failed to load salary rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/salary-rules`, rules, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Salary rules updated successfully');
      fetchRules();
    } catch (error) {
      toast.error('Failed to update salary rules');
    }
  };

  if (loading) {
    return <Layout><div className="text-center py-8">Loading...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-salary-rules-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Salary Rules</h1>
          <p className="text-muted-foreground mt-2">Configure salary calculation rules</p>
        </div>

        <Card data-testid="salary-rules-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="mr-2 h-5 w-5" />
              Salary Calculation Rules
            </CardTitle>
            <CardDescription>
              Set the rules for calculating employee salaries based on attendance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="late_threshold_minutes">Late Threshold (minutes)</Label>
                <Input
                  id="late_threshold_minutes"
                  type="number"
                  value={rules.late_threshold_minutes}
                  onChange={(e) => setRules({...rules, late_threshold_minutes: parseInt(e.target.value)})}
                  data-testid="input-late-threshold"
                />
                <p className="text-sm text-muted-foreground">
                  Minutes after which an employee is considered late
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lates_for_half_day">Late Count for Half Day</Label>
                <Input
                  id="lates_for_half_day"
                  type="number"
                  value={rules.lates_for_half_day}
                  onChange={(e) => setRules({...rules, lates_for_half_day: parseInt(e.target.value)})}
                  data-testid="input-lates-half-day"
                />
                <p className="text-sm text-muted-foreground">
                  Number of late arrivals that equal one half day deduction
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="working_hours_per_day">Working Hours per Day</Label>
                <Input
                  id="working_hours_per_day"
                  type="number"
                  step="0.1"
                  value={rules.working_hours_per_day}
                  onChange={(e) => setRules({...rules, working_hours_per_day: parseFloat(e.target.value)})}
                  data-testid="input-working-hours"
                />
                <p className="text-sm text-muted-foreground">
                  Standard working hours per day
                </p>
              </div>

              <div className="pt-4 p-4 bg-secondary rounded-lg space-y-2">
                <h3 className="font-semibold">Current Rules Summary:</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Present = Full day salary</li>
                  <li>• Absent = Deducted from paid leaves first, then from salary</li>
                  <li>• {rules.lates_for_half_day} Late arrivals = 1 Half day deduction</li>
                  <li>• Late threshold: {rules.late_threshold_minutes} minutes</li>
                  <li>• Working hours: {rules.working_hours_per_day} hours/day</li>
                </ul>
              </div>

              <Button type="submit" className="w-full" data-testid="save-rules-button">
                Save Rules
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
